<script setup lang="ts">
// SRV › Attachments › a location (docs/STORAGE.md §2.6): its name, and how much space
// attachments may use there. Server storage holds attachments only (HRTH-183): libraries live
// in their owners' buckets and nodes. Sizes are typed in GB, blank for no limit, stored in MiB.
import { computed, ref } from 'vue'
import { describeError } from '../api/http'
import type { StorageLocation } from '../api/types'
import { gbText, mbFromGb, whereIs } from '../lib/memberStorage'
import { formatBytes } from '../lib/pattern'
import { useSession } from '../stores/session'
import Modal from './Modal.vue'

const props = defineProps<{ location: StorageLocation }>()
const emit = defineEmits<{ close: []; saved: [] }>()
const session = useSession()
const busy = ref(false)

const name = ref(props.location.name)
const gb = ref(gbText(props.location.policy?.attachments_mb))
const mb = computed(() => mbFromGb(gb.value))
const used = computed(() => {
  const bytes = formatBytes(props.location.used?.attachments ?? 0)
  return mb.value == null ? `${bytes} · no limit` : `${bytes} of ${formatBytes((mb.value ?? 0) * 1024 * 1024)}`
})

async function run(fn: () => Promise<unknown>, ok?: string): Promise<void> {
  busy.value = true
  try {
    await fn()
    if (ok) session.toast(ok)
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const rename = () =>
  run(async () => {
    await session.api.renameStorage(props.location.id, name.value.trim())
    emit('saved')
  }, 'Renamed')

const save = () =>
  run(async () => {
    await session.api.setLocationPolicy(props.location.id, { attachments_mb: mb.value ?? null })
    emit('saved')
  }, 'Saved')
</script>

<template>
  <Modal page :title="location.name" @close="emit('close')">
    <div class="space-y-6" data-testid="location-rules">
      <p class="mono text-text-muted">{{ location.kind === 's3' ? 'S3 bucket' : 'Folder on the server' }} · {{ whereIs(location) }}</p>

      <form v-if="!location.configured" class="flex gap-2" @submit.prevent="rename">
        <input v-model="name" class="input min-w-0 flex-1" maxlength="48" aria-label="Name" data-testid="location-name" />
        <button class="btn-ghost shrink-0" :disabled="busy || !name.trim() || name.trim() === location.name" data-testid="location-rename">Rename</button>
      </form>

      <form class="space-y-2" @submit.prevent="save">
        <label class="label" for="rule-attachments">Space for attachments</label>
        <div class="flex items-center gap-2">
          <input id="rule-attachments" v-model="gb" inputmode="decimal" class="input w-28" placeholder="no limit" data-testid="rule-attachments" />
          <span class="mono text-text-muted">GB · {{ used }} used</span>
        </div>
        <p class="mono text-text-muted">Counts only while attachments go here. Space frees up as they're delivered.</p>
        <p v-if="mb === undefined" class="mono text-error">Sizes are numbers of GB, or blank for no limit.</p>
        <button class="btn-primary" :disabled="busy || mb === undefined" data-testid="rule-save">Save</button>
      </form>
    </div>
  </Modal>
</template>
